import { api, ApiError } from "./api.js";
import { authScreen } from "./ui/auth.js";
import { lobbyScreen } from "./ui/lobby.js";
import { gameScreen } from "./ui/game.js";
import { matchLobbyScreen } from "./ui/matchLobby.js";
import { adminScreen } from "./ui/admin.js";
import { builderScreen } from "./ui/builder.js";
import { mount } from "./ui/dom.js";
import type { MatchmadeMode, MeResponse } from "@webolo/shared";

// Single admin account by username. Mirrors the server-side
// requireAdmin gate; the server is the source of truth, but checking
// client-side lets us redirect non-admins immediately without a flash.
const ADMIN_USERNAME = "bolo";
function isAdminUser(me: MeResponse): boolean {
  return me.username.toLowerCase() === ADMIN_USERNAME;
}
function isAdminRoute(): boolean {
  return window.location.hash === "#admin";
}
// /builder maps to #builder under the hash router so a deep link from the
// outside still loads the SPA shell first. The redirect on hashchange keeps
// the path in the URL bar clean for the admin while typing.
function isBuilderRoute(): boolean {
  return window.location.hash === "#builder";
}
function clearAdminHash(): void {
  if (isAdminRoute() || isBuilderRoute()) {
    history.replaceState(null, "", window.location.pathname + window.location.search);
  }
}

const root = document.getElementById("app");
if (!root) throw new Error("#app missing");

// Deep-link paths: webolo.net/builder and /admin should boot into the
// corresponding hash route without making the user manually type #foo.
// The SPA fallback (prod) and Vite (dev) both serve index.html for any
// path, so by the time this runs we just need to swap the URL.
(() => {
  const p = window.location.pathname.replace(/\/$/, "");
  if (p === "/builder" || p === "/admin") {
    const hash = p === "/builder" ? "#builder" : "#admin";
    history.replaceState(null, "", "/" + window.location.search + hash);
  }
})();

async function showAuth(): Promise<void> {
  mount(root!, authScreen(() => void showLobby()));
}

export function isMobile(): boolean {
  return (
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints > 1 && window.innerWidth < 1024)
  );
}

async function showLobby(): Promise<void> {
  let me: MeResponse;
  try {
    me = await api.me();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      await showAuth();
      return;
    }
    throw e;
  }
  // Hash routes: bolo gets the admin panel + map builder, everyone else
  // is bounced back to the lobby.
  if (isAdminRoute()) {
    if (isAdminUser(me)) {
      mount(root!, adminScreen(() => {
        clearAdminHash();
        void showLobby();
      }));
      return;
    }
    clearAdminHash();
  }
  if (isBuilderRoute()) {
    if (isAdminUser(me)) {
      mount(root!, builderScreen(() => {
        clearAdminHash();
        void showLobby();
      }));
      return;
    }
    clearAdminHash();
  }
  mount(
    root!,
    lobbyScreen(
      me,
      (gameId, team) => void showGame(gameId, team),
      (mode) => void showMatchmake(mode),
      () => void showAuth(),
      () => {
        window.location.hash = "#admin";
        void showLobby();
      },
    ),
  );
}

async function showGame(gameId: string, team: number): Promise<void> {
  mount(root!, gameScreen(gameId, team, () => void showLobby()));
}

async function showMatchmake(mode: MatchmadeMode): Promise<void> {
  let res;
  try {
    res = await api.matchmake(mode);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      await showAuth();
      return;
    }
    await showLobby();
    return;
  }
  mount(
    root!,
    matchLobbyScreen(
      res.gameId,
      res.team,
      res.warmupEndsAt,
      res.mode,
      () => void showLobby(),
    ),
  );
}

// React to hash changes so manual /#admin navigation works.
window.addEventListener("hashchange", () => { void showLobby(); });

// Boot: if we already have a session cookie, skip straight to the lobby.
(async () => {
  try {
    await api.me();
    await showLobby();
  } catch {
    await showAuth();
  }
})();
